import React from 'react';
import {
  Button,
  Checkbox,
  Input,
  Message,
  Radio,
  RadioGroup,
  Table,
  Tooltip,
  Whisper,
  useToaster,
} from 'rsuite';
import ColumnsButton from '../../common/ColumnsButton';
import FilterPanel, { FilterField } from '../../common/FilterPanel';
import * as trainersApi from '../../api/trainers';
import EditIcon from '@rsuite/icons/Edit';
import DeleteIcon from '@rsuite/icons/Trash';
import UploadImageModal from '../../common/UploadImageModal';
import { useFormErrors } from '../../common/useFormErrors';
import { useTableColumns } from '../../common/useTableColumns';
import { useConfirm } from '../../common/useConfirm';
import { API_DOMAIN } from '../../config';
import CreateTrainerModal from './CreateTrainerModal';
import EditTrainerModal from './EditTrainerModal';
import { SortType } from 'rsuite-table';
import PageLayout from '../../common/PageLayout';
import TablePagination from '../../common/TablePagination';
import { formatDate, getRowClassName } from '../../common/formatters';
import { renderRowNumberColumn } from '../../common/RowNumberColumn';
import { renderTableEmpty, renderTableLoading } from '../../common/TableStates';

const { Column, HeaderCell, Cell } = Table;

const TABLE_COLUMNS = [
  { value: 'name', label: "Ім'я", display: true, flexGrow: 2, minWidth: 140 },
  {
    value: 'active',
    label: 'Статус',
    display: true,
    flexGrow: 1,
    minWidth: 70,
  },
  {
    value: 'image',
    label: 'Зображення',
    display: true,
    flexGrow: 1,
    minWidth: 120,
  },
  {
    value: 'description',
    label: 'Опис',
    display: true,
    flexGrow: 4,
    minWidth: 200,
  },
  {
    value: 'createdAt',
    label: 'Створено',
    display: false,
    flexGrow: 1,
    minWidth: 90,
  },
  {
    value: 'updatedAt',
    label: 'Оновлено',
    display: true,
    flexGrow: 1,
    minWidth: 90,
  },
];

const stripHtmlForTable = (value?: string): string => {
  if (!value) {
    return '';
  }

  return value
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/\s+/g, ' ')
    .trim();
};

/** Status filter options for the trainers list. */
type StatusFilter = 'all' | 'active' | 'inactive';

export default function Trainers() {
  const toaster = useToaster();
  const [confirm, ConfirmDialog] = useConfirm();

  const [trainers, setTrainers] = React.useState<any[]>([]);
  const [page, setPage] = React.useState(1);
  const [perPage, setPerPage] = React.useState(15);

  const [sortBy, setSortBy] = React.useState('createdAt');
  const [sortOrder, setSortOrder] = React.useState<SortType>('desc');
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<StatusFilter>('all');

  const [tableColumns, , toggleColumn] = useTableColumns(
    TABLE_COLUMNS,
    'trainers:tableColumns'
  );

  const [updateModalObject, setUpdateModalObject] = React.useState<any | null>(
    null
  );
  const [createModalOpen, setCreateModalOpen] = React.useState<boolean>(false);
  const [updateTrainerImageObj, setUpdateTrainerImageObj] =
    React.useState<null | {
      id?: string;
      image?: string;
    }>(null);
  const {
    errorSummary: updateErrors,
    setApiError: setUpdateError,
    clearErrors: clearUpdateErrors,
  } = useFormErrors({
    fallbackMessage: 'Не вдалося оновити тренера. Спробуйте пізніше',
    resetOn: [updateModalObject?.id],
  });
  const handleUpdateImageModalClose = () => {
    setUpdateTrainerImageObj(null);
    getData()
      .catch(console.error)
      .then(() => setLoading(false));
  };

  const clickedCellRef = React.useRef<boolean>(false);

  const getData = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await trainersApi.list(sortBy, sortOrder.toUpperCase());
      setTrainers(response.trainers);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder]);

  const updateTrainer = async () => {
    if (!updateModalObject) {
      return;
    }

    clearUpdateErrors();
    try {
      await trainersApi.update(
        updateModalObject.id,
        updateModalObject.slug,
        updateModalObject.title,
        updateModalObject.titleEn,
        updateModalObject.titleDe,
        updateModalObject.name,
        updateModalObject.nameEn,
        updateModalObject.nameDe,
        updateModalObject.description,
        updateModalObject.descriptionEn,
        updateModalObject.descriptionDe,
        updateModalObject.shortDescription,
        updateModalObject.shortDescriptionEn,
        updateModalObject.shortDescriptionDe,
        updateModalObject.priority,
        updateModalObject.active,
        updateModalObject.isFeatured
      );
      toaster.push(
        <Message type="success" showIcon closable>
          Тренера оновлено
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
      setUpdateModalObject(null);
      getData()
        .catch(console.error)
        .then(() => setLoading(false));
    } catch (err: any) {
      console.error(err);
      const errorMessage = setUpdateError(err)[0];
      toaster.push(
        <Message type="error" showIcon closable>
          {errorMessage}
        </Message>,
        {
          placement: 'topEnd',
          duration: 1500,
        }
      );
    }
  };

  React.useEffect(() => {
    getData().catch(console.error);
  }, [getData]);

  const filteredTrainers = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return trainers.filter(trainer => {
      if (
        query &&
        !trainer.name?.toLowerCase().includes(query) &&
        !trainer.title?.toLowerCase().includes(query)
      ) {
        return false;
      }
      if (statusFilter === 'active' && !trainer.active) {
        return false;
      }
      if (statusFilter === 'inactive' && trainer.active) {
        return false;
      }
      return true;
    });
  }, [search, trainers, statusFilter]);

  const totalPages = React.useMemo(() => {
    if (!filteredTrainers.length) {
      return 0;
    }
    return Math.ceil(filteredTrainers.length / perPage);
  }, [filteredTrainers.length, perPage]);

  React.useEffect(() => {
    setPage(1);
  }, [search, perPage, statusFilter]);

  React.useEffect(() => {
    if (totalPages === 0) {
      if (page !== 1) {
        setPage(1);
      }
      return;
    }
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const pagedTrainers = React.useMemo(() => {
    const safePage = page > 0 ? page : 1;
    const offset = (safePage - 1) * perPage;
    return filteredTrainers.slice(offset, offset + perPage);
  }, [filteredTrainers, page, perPage]);

  const firstVisibleColumnValue = React.useMemo(
    () => tableColumns.find(column => column.display !== false)?.value,
    [tableColumns]
  );

  const renderColumn = (column: (typeof TABLE_COLUMNS)[0]) => {
    if (column.display === false) {
      return null;
    }

    const commonProps = {
      key: column.value,
      flexGrow: column.flexGrow ?? 1,
      minWidth: column.minWidth || 80,
      fixed:
        column.value === firstVisibleColumnValue
          ? ('left' as const)
          : undefined,
    };

    switch (column.value) {
      case 'name':
        return (
          <Column {...commonProps} fullText={true} sortable={true}>
            <HeaderCell>Ім'я</HeaderCell>
            <Cell dataKey="name" />
          </Column>
        );
      case 'active':
        return (
          <Column {...commonProps} sortable={true}>
            <HeaderCell>Статус</HeaderCell>
            <Cell
              onClick={() => {
                clickedCellRef.current = true;
              }}
              dataKey="active"
            >
              {rowData => (
                <Checkbox
                  checked={rowData.active}
                  onChange={(_value, checked) => {
                    setTrainers(prev =>
                      prev.map(t =>
                        t.id === rowData.id ? { ...t, active: checked } : t
                      )
                    );
                    trainersApi.setActive(rowData.id, checked).catch(err => {
                      console.error(err);
                      setTrainers(prev =>
                        prev.map(t =>
                          t.id === rowData.id ? { ...t, active: !checked } : t
                        )
                      );
                    });
                  }}
                />
              )}
            </Cell>
          </Column>
        );
      case 'isFeatured':
        return (
          <Column {...commonProps} sortable={true}>
            <HeaderCell>Обраний</HeaderCell>
            <Cell dataKey="isFeatured">
              {rowData => (
                <Checkbox checked={rowData.isFeatured} disabled={true} />
              )}
            </Cell>
          </Column>
        );
      case 'image':
        return (
          <Column {...commonProps}>
            <HeaderCell>Зображення</HeaderCell>
            <Cell>
              {rowData => (
                <div>
                  <img
                    onClick={() => {
                      clickedCellRef.current = true;
                      setUpdateTrainerImageObj({
                        id: rowData.id,
                        image: rowData.image,
                      });
                    }}
                    style={{
                      objectFit: 'contain',
                      width: '100%',
                      height: '100%',
                    }}
                    src={rowData.image}
                  />
                </div>
              )}
            </Cell>
          </Column>
        );
      case 'description':
        return (
          <Column {...commonProps} sortable={true}>
            <HeaderCell>Опис</HeaderCell>
            <Cell dataKey="description" className="table-cell-description">
              {rowData => {
                const plainDescription = stripHtmlForTable(rowData.description);
                return (
                  <div
                    style={{
                      minHeight: 40,
                      maxHeight: 140,
                      overflow: 'auto',
                    }}
                  >
                    {plainDescription || '-'}
                  </div>
                );
              }}
            </Cell>
          </Column>
        );
      case 'createdAt':
        return (
          <Column {...commonProps} sortable={true}>
            <HeaderCell>Створено</HeaderCell>
            <Cell dataKey="createdAt">
              {rowData => formatDate(rowData.createdAt)}
            </Cell>
          </Column>
        );
      case 'updatedAt':
        return (
          <Column {...commonProps} sortable={true}>
            <HeaderCell>Оновлено</HeaderCell>
            <Cell dataKey="updatedAt">
              {rowData => formatDate(rowData.updatedAt)}
            </Cell>
          </Column>
        );
      default:
        return null;
    }
  };

  return (
    <>
      {ConfirmDialog}
      <PageLayout
        title="Тренери"
        actions={
          <>
            <Input
              className="toolbar-search"
              placeholder="Пошук за ім'ям або заголовком"
              value={search}
              onChange={setSearch}
            />
            <FilterPanel
              activeCount={statusFilter !== 'all' ? 1 : 0}
              onReset={() => setStatusFilter('all')}
            >
              {() => (
                <FilterField label="Статус">
                  <RadioGroup
                    name="trainer-status"
                    value={statusFilter}
                    onChange={value => setStatusFilter(value as StatusFilter)}
                  >
                    <Radio value="all">Всі</Radio>
                    <Radio value="active">Активні</Radio>
                    <Radio value="inactive">Неактивні</Radio>
                  </RadioGroup>
                </FilterField>
              )}
            </FilterPanel>
            <ColumnsButton columns={tableColumns} onToggle={toggleColumn} />
            <Button
              appearance="primary"
              onClick={() => setCreateModalOpen(true)}
            >
              Створити
            </Button>
          </>
        }
      >
        <div className="section-card">
          <Table
            style={{ width: '100%' }}
            loading={loading}
            autoHeight={true}
            affixHeader={true}
            affixHorizontalScrollbar={true}
            bordered={true}
            data={pagedTrainers}
            renderLoading={renderTableLoading}
            renderEmpty={renderTableEmpty}
            rowClassName={getRowClassName}
            onRowClick={(rowData, evet) => {
              if (clickedCellRef.current) {
                clickedCellRef.current = false;
                return;
              }
              setUpdateModalObject(rowData);
            }}
            sortColumn={sortBy}
            sortType={sortOrder}
            onSortColumn={(sortColumn, sortType) => {
              setSortBy(sortColumn);
              setSortOrder(sortType || 'desc');
            }}
            wordWrap="break-word"
          >
            {renderRowNumberColumn({ page, perPage })}

            {tableColumns.map(renderColumn)}

            <Column fixed="right" width={100}>
              <HeaderCell>Дії</HeaderCell>
              <Cell
                onClick={() => {
                  clickedCellRef.current = true;
                }}
                style={{ padding: '4px 6px' }}
              >
                {rowData => (
                  <div className="action-icons">
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Редагувати</Tooltip>}
                    >
                      <Button
                        appearance="link"
                        onClick={() => setUpdateModalObject(rowData)}
                      >
                        <EditIcon />
                      </Button>
                    </Whisper>
                    <Whisper
                      placement="top"
                      trigger="hover"
                      speaker={<Tooltip>Видалити</Tooltip>}
                    >
                      <Button
                        appearance="subtle"
                        className="delete-btn"
                        onClick={async () => {
                          const result = await confirm({
                            message: `Ви впевнені, що хочете видалити тренера ${rowData.name}?`,
                            destructive: true,
                            confirmText: 'Видалити',
                          });
                          if (!result) {
                            return;
                          }
                          try {
                            await trainersApi.remove(rowData.id);
                            toaster.push(
                              <Message type="success" showIcon closable>
                                Тренера видалено
                              </Message>,
                              {
                                placement: 'topEnd',
                                duration: 1500,
                              }
                            );
                            getData()
                              .catch(console.error)
                              .then(() => setLoading(false));
                          } catch (err: any) {
                            console.error(err);
                            const errorMessage =
                              err.response?.data?.message ||
                              'Не вдалося видалити тренера. Спробуйте пізніше';
                            toaster.push(
                              <Message type="error" showIcon closable>
                                {errorMessage}
                              </Message>,
                              {
                                placement: 'topEnd',
                                duration: 1500,
                              }
                            );
                          }
                        }}
                      >
                        <DeleteIcon />
                      </Button>
                    </Whisper>
                  </div>
                )}
              </Cell>
            </Column>
          </Table>
          <TablePagination
            total={filteredTrainers.length}
            activePage={page}
            pages={totalPages}
            limit={perPage}
            limitOptions={[15, 30, 50, 100]}
            onChangeLimit={limit => {
              setPerPage(limit);
              setPage(1);
            }}
            onChangePage={setPage}
          />
        </div>
      </PageLayout>

      <CreateTrainerModal
        open={createModalOpen}
        setOpen={setCreateModalOpen}
        callback={() => {
          getData()
            .catch(console.error)
            .then(() => setLoading(false));
        }}
      />
      <EditTrainerModal
        modalTrainerObject={updateModalObject}
        setModalTrainerObject={setUpdateModalObject}
        handleModalClose={() => {
          setUpdateModalObject(null);
        }}
        submit={updateTrainer}
        errorSummary={updateErrors}
      />

      {updateTrainerImageObj?.id?.length && (
        <UploadImageModal
          title="Редагувати зображення тренера"
          actionUrl={`${API_DOMAIN}/api/trainers/${updateTrainerImageObj?.id}/image`}
          object={updateTrainerImageObj}
          handleModalClose={handleUpdateImageModalClose}
        />
      )}
    </>
  );
}
